// Detail-drawer panel showing the printable document of a record (invoice,
// POS receipt, payment note, delivery note) with Print / public-link buttons.

import { useState, type ComponentType } from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { Link } from 'iconsax-reactjs';

import type { CrudActionContext, CrudRecord } from '../../crud/types';
import { getRuntimeConfig } from '../../runtime';
import { t } from '../../i18n';
import { OrderDocument, type OrderDocumentKind } from './OrderDocument';
import { PaymentDocument } from './PaymentDocument';
import { PrintableDocument } from './PrintableDocument';
import { ReceiptDocument } from './ReceiptDocument';
import { UI } from '../../brand/ui';

export type DocumentKind = OrderDocumentKind | 'payment';

// Public (unauthenticated) view type used by GET /admin/views/{type}/{hash}.
const publicType: Partial<Record<DocumentKind, string>> = {
  sale: 'sale',
  purchase: 'purchase',
  quotation: 'quotation',
  return_order: 'return_order',
  payment: 'payment'
};

export function publicViewUrl(kind: DocumentKind, hash: unknown) {
  const type = publicType[kind];
  if (!type || !hash) return null;
  const base = getRuntimeConfig().basePath.replace(/\/$/, '');
  return `${window.location.origin}${base}/views/${type}/${hash}`;
}

type DetailExtraProps = { record: CrudRecord; reload: () => Promise<void>; ctx: CrudActionContext };

export function DocumentPanel({ record, kind, ctx, initialTab }: { record: CrudRecord; kind: DocumentKind; ctx?: CrudActionContext; initialTab?: 'invoice' | 'receipt' }) {
  const isPos = kind === 'sale' && Number(record.pos) === 1;
  const [tab, setTab] = useState<'invoice' | 'receipt'>(initialTab ?? 'invoice');
  const link = publicViewUrl(kind, record.hash);
  const title = `${t(`doc.kind.${kind}`)} ${String(record.reference ?? '')}`.trim();

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      ctx?.toast(t('doc.linkCopied'), 'success');
    } catch {
      window.open(link, '_blank', 'noopener');
    }
  };

  const actions = link ? (
    <Button variant="outlined" startIcon={<Link size={16} />} onClick={() => void copyLink()}>
      {t('doc.copyPublicLink')}
    </Button>
  ) : null;

  return (
    <Box sx={{ border: `1px solid ${UI.border}`, borderRadius: '10px', overflow: 'hidden', bgcolor: UI.white }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: UI.accentAlpha(0.06), borderBottom: `1px solid ${UI.border}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: UI.ink }}>
          {t('doc.printable')}
        </Typography>
      </Box>
      {isPos ? (
        <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 2 }}>
          <Tab value="invoice" label={t('doc.invoice')} />
          <Tab value="receipt" label={t('doc.receipt')} />
        </Tabs>
      ) : null}
      <Box sx={{ p: 2 }}>
        {kind === 'payment' ? (
          <PrintableDocument title={title} actions={actions}>
            <PaymentDocument record={record} />
          </PrintableDocument>
        ) : isPos && tab === 'receipt' ? (
          <PrintableDocument title={title} variant="receipt" actions={actions}>
            <ReceiptDocument record={record} />
          </PrintableDocument>
        ) : (
          <PrintableDocument title={title} actions={actions}>
            <OrderDocument record={record} kind={kind} />
          </PrintableDocument>
        )}
      </Box>
    </Box>
  );
}

// Build a `detailExtra` component for a resource config, chaining any existing one.
export function documentDetailExtra(kind: DocumentKind, previous?: ComponentType<DetailExtraProps>): ComponentType<DetailExtraProps> {
  function DocumentDetailExtra(props: DetailExtraProps) {
    const Previous = previous;
    return (
      <>
        {Previous ? <Previous {...props} /> : null}
        <DocumentPanel record={props.record} kind={kind} ctx={props.ctx} />
      </>
    );
  }
  return DocumentDetailExtra;
}
